import { Icon } from "@farm-junction/design-library";

export type CarVariantColorTag = {
    id: string;
    name: string;
    slug?: string;
    label: string;
};

export type CarVariantColorItem = {
    id: string;
    name: string;
    hex: string;
    image?: string;
    variants: CarVariantColorTag[];
};

export type CarVariantColorCardProps = {
    colour: CarVariantColorItem;
    selected?: boolean;
    onClick?: () => void;
};

const VISIBLE_VARIANT_COUNT = 3;

export default function CarVariantColorCard({
    colour,
    selected = false,
    onClick,
}: CarVariantColorCardProps) {
    const variants = colour.variants.filter(
        (variant, index, all) => all.findIndex((item) => item.id === variant.id) === index,
    );
    const visibleVariants = variants.slice(0, VISIBLE_VARIANT_COUNT);
    const hiddenCount = variants.length - visibleVariants.length;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={`View ${colour.name} colour variants`}
            aria-pressed={selected}
            className={`w-full cursor-pointer rounded-[14px] border-[1px] bg-white p-3.5 text-left transition-colors md:p-4 ${
                selected
                    ? "border-[#008C8B]"
                    : "border-[#E5E7EB] hover:border-[#008c8b] hover:bg-[#F9F9F9]"
            }`}
        >
            <div className="flex items-center gap-3">
                <span
                    aria-hidden
                    className="block h-8 w-8 shrink-0 rounded-full border-[1px] border-black/10 md:h-9 md:w-9"
                    style={{ background: colour.hex }}
                />
                <span className="min-w-0 flex-1 truncate text-[14px] font-semibold text-[#16181D] md:text-[15px]">
                    {colour.name}
                </span>
                <Icon name="rightCaret" width={10} height={10} fill="#9AA0A6" className="shrink-0" />
            </div>

            {variants.length > 0 && (
                <div className="mt-3 border-t border-[#E5E7EB] pt-3">
                    <div className="flex min-w-0 items-center gap-1">
                        {visibleVariants.map((variant, index) => (
                            <span
                                key={`${variant.id}-${index}`}
                                title={variant.name}
                                className="min-w-[22px] shrink truncate whitespace-nowrap rounded-full border-[1px] border-[#E5E7EB] bg-[#F4F5F6] px-1.5 py-0.5 text-center text-[11px] font-medium leading-4 text-[#4B5563]"
                            >
                                {variant.name || variant.label}
                            </span>
                        ))}
                        {hiddenCount > 0 && (
                            <span className="shrink-0 whitespace-nowrap rounded-full bg-[#E6F6F6] px-1.5 py-0.5 text-[11px] font-semibold leading-4 text-[#008C8B]">
                                {`+${hiddenCount} ${hiddenCount === 1 ? "variant" : "variants"}`}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </button>
    );
}
